
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Ticket, CheckCircle, AlertCircle } from 'lucide-react';
import { api } from '@/lib/api/client';

const inviteCodeSchema = z.object({
  inviteCode: z.string()
    .min(6, 'Invite code must be at least 6 characters')
    .max(32, 'Invite code must be less than 32 characters')
    .regex(/^[A-Z0-9-]+$/i, 'Invite code can only contain letters, numbers, and hyphens'),
});

type InviteCodeFormData = z.infer<typeof inviteCodeSchema>;

interface InviteCodeRedemptionProps {
  streamId: number;
  streamTitle?: string;
  onAccessGranted?: () => void;
  variant?: 'inline' | 'dialog';
}

export function InviteCodeRedemption({ 
  streamId, 
  streamTitle, 
  onAccessGranted,
  variant = 'dialog' 
}: InviteCodeRedemptionProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm<InviteCodeFormData>({
    resolver: zodResolver(inviteCodeSchema),
    defaultValues: {
      inviteCode: '',
    },
  });

  const onSubmit = async (data: InviteCodeFormData) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await api.post('api/invites/join', {
        code: data.inviteCode.toUpperCase(),
        streamId,
      });

      if (response.success) {
        setIsSuccess(true);
        form.reset();
      } else {
        throw new Error(response.error?.message || 'Failed to redeem invite code');
      }
      
      // Call callback after short delay to show success state
      setTimeout(() => {
        onAccessGranted?.();
        setIsOpen(false);
      }, 1500);

    } catch (err) {
      console.error('Redeem invite code error:', err);
      setError(err instanceof Error ? err.message : 'Failed to redeem invite code');
    } finally {
      setIsLoading(false);
    }
  };

  const formContent = (
    <>
      {isSuccess ? (
        <div className="flex flex-col items-center justify-center space-y-4 py-6 text-center">
          <CheckCircle className="h-12 w-12 text-green-500" />
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">Access Granted!</h3>
            <p className="text-sm text-muted-foreground">
              You now have access to this stream. Enjoy watching!
            </p>
          </div>
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <FormField
              control={form.control}
              name="inviteCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Invite Code</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter your invite code (e.g., ABC-123-XYZ)"
                      {...field}
                      onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                      disabled={isLoading}
                      className="font-mono tracking-wider"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Verifying...
                </>
              ) : (
                <>
                  <Ticket className="h-4 w-4 mr-2" />
                  Redeem Invite Code
                </>
              )}
            </Button>
          </form>
        </Form>
      )}
    </>
  );

  if (variant === 'inline') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ticket className="h-5 w-5" />
            Have an Invite Code?
          </CardTitle>
          <CardDescription>
            Enter your invite code to gain access to this stream
          </CardDescription>
        </CardHeader>
        <CardContent>
          {formContent}
        </CardContent>
      </Card>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Ticket className="h-4 w-4 mr-2" />
          Use Invite Code
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ticket className="h-5 w-5" />
            Redeem Invite Code
          </DialogTitle>
          <DialogDescription>
            {streamTitle 
              ? `Enter your invite code to access "${streamTitle}"`
              : 'Enter your invite code to gain access to this stream'
            }
          </DialogDescription>
        </DialogHeader>
        {formContent}
      </DialogContent>
    </Dialog>
  );
}
